import { Dispatch } from '@reduxjs/toolkit';
import { memoize } from 'lodash';
import { AnyAction } from 'redux';
import { getCreditApplication, getCreditApplicationFilteredList } from '../../services/api/miles/miles-credit-services';
import { getContracts, getPendingContracts, getQuotes } from '../../services/api/miles/miles-services';
import { loadingActions } from '../../services/redux/features/loading.slice';
import { setConversations, setCurrentConversation } from '../../services/redux/features/notes.slice';
import { ConversationService } from './conversationService';

/**
 * Gets the first participant of the specified type from a conversation
 * @param conversation The conversation object to search in
 * @param participantType The type of participant to find (BACK_OFFICE or FRONT_OFFICE)
 * @returns The first participant of the specified type, or undefined if none found
 */
export const getFirstParticipantOfType = (
  conversation: MilesPosNotesIntegrationService.Components.Schemas.Conversation,
  participantType: MilesPosNotesIntegrationService.Components.Schemas.ParticipantType
): MilesPosNotesIntegrationService.Components.Schemas.Participant | undefined => {
  return conversation.participants?.find((participant) => participant.participantType === participantType);
};

/**
 * Gets the first message of type TEXT_MESSAGE from a conversation
 * @param conversation The conversation object to search in
 * @returns The first TEXT_MESSAGE message, or undefined if none found
 */
export const getFirstTextMessage = (
  conversation: MilesPosNotesIntegrationService.Components.Schemas.Conversation
): MilesPosNotesIntegrationService.Components.Schemas.Message | undefined => {
  if (conversation.lastMessage?.messageType === 'TEXT_MESSAGE') {
    return conversation.lastMessage;
  }
  return undefined;
};

/**
 * Checks if any participant in the array has isCurrentUser flag set to true
 * @param participants Array of conversation participants to check
 * @returns True if any participant has isCurrentUser=true, false otherwise
 */
export const isCurrentUserParticipant = (
  participants?: MilesPosNotesIntegrationService.Components.Schemas.Participant[]
): boolean => {
  if (!participants || participants.length === 0) {
    return false;
  }
  return participants.some((participant) => participant.isCurrentUser === true);
};

/**
 * Gets the initiator participant from a list of participants
 * @param participants Array of conversation participants to search in
 * @returns The initiator participant, or undefined if none found
 */
export const getInitiatorParticipant = (
  participants?: MilesPosNotesIntegrationService.Components.Schemas.Participant[]
): MilesPosNotesIntegrationService.Components.Schemas.Participant | undefined => {
  return participants?.find((participant) => participant.isInitiator);
};

export const _fetchObjectMetadata = async (
  humanReadableObjectReferenceId: string, // Human readable reference id
  objectType: MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum
): Promise<{
  entityFullName: string | undefined;
  systemObjectId: string | undefined;
  humanReadableObjectReferenceId: string | undefined;
}> => {
  if (!objectType || !humanReadableObjectReferenceId) {
    return { entityFullName: undefined, systemObjectId: undefined, humanReadableObjectReferenceId: undefined };
  }

  switch (objectType) {
    case 'SALES_QUOTE': {
      const getQuoteListResponse = await getQuotes({
        offerReference: humanReadableObjectReferenceId,
      });
      if (getQuoteListResponse?.quotes && getQuoteListResponse?.quotes?.length > 0) {
        const quote: QuoteSchema.Schemas.QuoteResponse = getQuoteListResponse.quotes[0];
        const entityFullName = quote.customerData?.tradingName || quote.customerData?.companyData?.name || '';
        const sourceId = quote.financialProduct?.offers?.[0]?.offerId;
        return { entityFullName, systemObjectId: sourceId, humanReadableObjectReferenceId };
      }
      throw new Error(`Quote not found for reference: ${humanReadableObjectReferenceId}`);
    }
    case 'PENDING_CONTRACT': {
      const requestBody = { searchQuery: humanReadableObjectReferenceId };
      const pendingContractsResponse = await getPendingContracts(requestBody);
      if (pendingContractsResponse?.financialProduct?.pendingContracts?.length > 0) {
        const contract = pendingContractsResponse.financialProduct.pendingContracts[0];
        const entityFullName = contract.applicants?.[0]?.tradingName || '';
        const sourceId = contract.contractId;
        return { entityFullName, systemObjectId: sourceId, humanReadableObjectReferenceId };
      }
      throw new Error(`Pending contract not found for search query: ${humanReadableObjectReferenceId}`);
    }
    case 'CONTRACT': {
      const getContractsResponse = await getContracts({ searchQuery: humanReadableObjectReferenceId });
      if (getContractsResponse?.quotes?.length > 0 && getContractsResponse.quotes[0].applicants) {
        const entityFullName = getContractsResponse.quotes[0].applicants[0]?.tradingName || '';
        const sourceId = getContractsResponse?.quotes[0].contractId;
        return { entityFullName, systemObjectId: sourceId, humanReadableObjectReferenceId };
      }
      throw new Error(`Contract not found for search query: ${humanReadableObjectReferenceId}`);
    }
    case 'CREDIT_APPLICATION': {
      let creditApplicationResponse: CreditIntegration.Schemas.GetListCreditApplicationsResponse;
      let humanReadableObjectReferenceIdToUse = humanReadableObjectReferenceId;

      try {
        // This section is hacky, we can remap here, or flood API with remaping requests in getReferenceId() function
        if (!humanReadableObjectReferenceId.includes('/')) {
          // It is not offer reference, but raw credit application id, we need to map back to offer reference
          const creditApplicationId = humanReadableObjectReferenceId;
          const creditApplication = await getCreditApplication(creditApplicationId);

          if (!creditApplication.financialProduct.offers[0].offerReference) {
            throw new Error(`Offer reference not found for credit application ID: ${creditApplicationId}`);
          }

          humanReadableObjectReferenceIdToUse = creditApplication.financialProduct.offers[0].offerReference;
        }
        // Then it is offer reference, user provided it in reference input filter or going from dashboard

        creditApplicationResponse = await getCreditApplicationFilteredList({
          searchQuery: humanReadableObjectReferenceIdToUse,
        });

        // Check if response is empty object
        if (!creditApplicationResponse || Object.keys(creditApplicationResponse).length === 0) {
          throw new Error(`Empty response for credit application ID: ${humanReadableObjectReferenceIdToUse}`);
        }

        // Check if creditApplications array is empty or undefined
        if (
          !creditApplicationResponse.creditApplications ||
          creditApplicationResponse.creditApplications.length === 0
        ) {
          throw new Error(`No credit applications found for ID: ${humanReadableObjectReferenceIdToUse}`);
        }

        const creditApplication = creditApplicationResponse?.creditApplications?.[0];
        const entityFullName = creditApplication?.customerData?.tradingName;
        const sourceId = creditApplication?.creditDecision?.solvencyCheck?.decisionIdentifier;

        if (entityFullName && sourceId) {
          return {
            entityFullName,
            systemObjectId: sourceId,
            humanReadableObjectReferenceId: humanReadableObjectReferenceIdToUse,
          };
        }
      } catch (e) {
        throw new Error(`Credit application not found for ID: ${humanReadableObjectReferenceIdToUse}`);
      }

      throw new Error(`Unknow Credit application error ID: ${humanReadableObjectReferenceIdToUse}`);
    }
    default:
      throw new Error(`Unsupported representation ID: ${objectType}`);
  }
};

// WARNING: Lodash dependency! Without memoization we would flood API with requests
export const fetchObjectMetadata = memoize(
  _fetchObjectMetadata,
  (
    humanReadableObjectReferenceId: string,
    objectType: MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum
  ) => `${humanReadableObjectReferenceId}_${objectType}`
);

export const getReferenceData = (
  conversation: MilesPosNotesIntegrationService.Components.Schemas.Conversation
):
  | {
      humanReadableObjectReferenceId: string;
      objectType: MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum;
    }
  | undefined => {
  // In order of precedence
  if (conversation.referenceMap?.salesQuoteReference) {
    return {
      humanReadableObjectReferenceId: conversation.referenceMap.salesQuoteReference,
      objectType: 'SALES_QUOTE',
    };
  }

  if (conversation.referenceMap?.pendingContractReference) {
    return {
      humanReadableObjectReferenceId: conversation.referenceMap.pendingContractReference,
      objectType: 'PENDING_CONTRACT',
    };
  }

  if (conversation.referenceMap?.contractReference) {
    return {
      humanReadableObjectReferenceId: conversation.referenceMap.contractReference,
      objectType: 'CONTRACT',
    };
  }

  if (conversation.referenceMap?.creditApplicationReference) {
    return {
      humanReadableObjectReferenceId: conversation.referenceMap.creditApplicationReference,
      objectType: 'CREDIT_APPLICATION',
    };
  }

  return undefined;
};

/**
 * Translates reference type keys to human-readable labels using filters translations
 * @param referenceType The reference type to translate
 * @param t Translation function from useTranslation hook
 * @returns Translated reference type label
 */
export const translateReferenceType = (
  referenceType: MilesPosNotesIntegrationService.Components.Schemas.CreateConversationContextRepresentation,
  t: (key: string) => string
): string => {
  const referenceTypeMap: Record<
    MilesPosNotesIntegrationService.Components.Schemas.ContextRepresentationsEnum,
    string
  > = {
    SALES_QUOTE: t('filters:salesQuote'),
    CREDIT_APPLICATION: t('filters:creditApplication'),
    PENDING_CONTRACT: t('filters:pendingContract'),
    CONTRACT: t('filters:contract'),
  };

  return referenceTypeMap[referenceType] || referenceType;
};

/**
 * Toggles the read/unread status of a conversation and updates the Redux store.
 *
 * @param dispatch - Redux dispatch function
 * @param conversationId - Id of the conversation to update
 * @param hasUnreadMessages - Whether the conversation currently has unread messages
 * @param conversations - Current list of conversations
 */

export const toggleUnreadStatus = async (
  dispatch: Dispatch<AnyAction>,
  conversationId: string,
  hasUnreadMessages: boolean,
  conversations: MilesPosNotesIntegrationService.Components.Schemas.Conversation[]
) => {
  dispatch(loadingActions.setLoading(true));

  try {
    const updatedConversation = await ConversationService.updateConversation(conversationId, {
      resetIsSeenByCurrentUser: !hasUnreadMessages,
    });

    dispatch(
      setConversations(
        conversations.map((conversation) =>
          conversation.conversationId === conversationId ? updatedConversation : conversation
        )
      )
    );

    dispatch(setCurrentConversation(updatedConversation));
  } finally {
    dispatch(loadingActions.setLoading(false));
  }
};
